import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Send, MessageSquare, Sparkles, Cat } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSafePostHog } from '../components/PostHogProvider';

const BehaviorChat = () => {
  const posthog = useSafePostHog();
  const [catProfiles, setCatProfiles] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { loadCatProfiles(); }, []);
  useEffect(() => { if (selectedCat) loadChatHistory(); }, [selectedCat]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadCatProfiles = async () => {
    try { const r = await api.get('/cat-profiles'); setCatProfiles(r.data); if (r.data.length > 0) setSelectedCat(r.data[0].id); }
    catch { console.error('Failed to load profiles'); }
  };

  const loadChatHistory = async () => {
    try { const r = await api.get(`/behavior-chat/${selectedCat}`); setMessages(r.data.messages || []); }
    catch { setMessages([]); }
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedCat) { if (!selectedCat) toast.error('Select a cat profile first'); return; }
    const userMsg = { role: 'user', content: input, timestamp: new Date().toISOString() };
    posthog.capture('chat_sent', { message_length: input.trim().length, cat_id: selectedCat });
    setMessages((prev) => [...prev, userMsg]); setInput(''); setLoading(true);
    try {
      const r = await api.post('/behavior-chat', { cat_id: selectedCat, message: input });
      setMessages((prev) => [...prev, { role: 'assistant', content: r.data.response, timestamp: new Date().toISOString() }]);
    } catch (error) {
      if (error.response?.status === 403) toast.error(error.response.data.detail);
      else toast.error('Failed to get response.');
      setMessages((prev) => prev.slice(0, -1));
    } finally { setLoading(false); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const selectedCatData = catProfiles.find((c) => c.id === selectedCat);

  return (
    <DashboardLayout title="Behavior Chat">
      <div className="flex gap-4 h-[calc(100dvh-10rem)] sm:h-[calc(100dvh-10rem)]" data-testid="chat-layout">

        {/* Left Sidebar — Cat Profiles (hidden on mobile) */}
        <div className="hidden md:flex flex-col w-64 shrink-0 bg-white border border-[#1B4332]/10 rounded-xl overflow-hidden" data-testid="cat-sidebar">
          <div className="px-4 py-3 border-b border-[#1B4332]/8">
            <p className="text-[11px] uppercase tracking-widest font-bold text-[#1B4332]/40">Your Cats</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {catProfiles.length === 0 ? (
              <div className="p-4 text-center">
                <Cat className="w-8 h-8 text-[#1B4332]/15 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#1B4332]/40">No cats added yet</p>
              </div>
            ) : (
              catProfiles.map((cat) => {
                const isActive = cat.id === selectedCat;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCat(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 border-l-[3px] ${
                      isActive
                        ? 'bg-[#1B4332]/5 border-l-[#1B4332]'
                        : 'border-l-transparent hover:bg-[#1B4332]/3'
                    }`}
                    data-testid={`cat-sidebar-${cat.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      isActive ? 'bg-[#1B4332] text-white' : 'bg-[#1B4332]/10 text-[#1B4332]'
                    }`}>
                      {cat.name?.charAt(0)?.toUpperCase() || '🐱'}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-[13px] font-semibold truncate ${isActive ? 'text-[#1B4332]' : 'text-[#1B4332]/70'}`}>
                        {cat.name}
                      </p>
                      <p className="text-[11px] text-[#1B4332]/40 truncate">
                        {cat.breed || 'Cat'}{cat.age ? ` · ${cat.age}y` : ''}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel — Chat Window */}
        <div className="flex-1 flex flex-col bg-white border border-[#1B4332]/10 rounded-xl overflow-hidden min-w-0 shadow-sm" data-testid="chat-container">

          {/* Chat Header */}
          <div className="px-4 py-3 border-b border-[#1B4332]/8 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              {selectedCatData ? (
                <>
                  <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center text-xs font-bold">
                    {selectedCatData.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#1B4332]">{selectedCatData.name}</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <p className="text-[11px] text-[#1B4332]/40">
                        {selectedCatData.breed || 'Cat'}{selectedCatData.age ? ` · ${selectedCatData.age}y` : ''} — AI Online
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-[13px] text-[#1B4332]/40">Select a cat to start chatting</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 bg-[#1B4332]/5 rounded-md px-2 py-1">
              <Sparkles className="w-3 h-3 text-[#1B4332]" strokeWidth={2} />
              <span className="text-[10px] font-semibold text-[#1B4332]/60 uppercase tracking-wider">PurrCheck AI</span>
            </div>
          </div>

          {/* Mobile cat selector (visible only on mobile) */}
          <div className="md:hidden px-3 py-2 border-b border-[#1B4332]/5 flex gap-2 overflow-x-auto scrollbar-hide">
            {catProfiles.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCat(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all ${
                  cat.id === selectedCat
                    ? 'bg-[#1B4332] text-white'
                    : 'bg-[#1B4332]/5 text-[#1B4332]/60'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-[#F8FAF9]" data-testid="messages-container">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-12 h-12 rounded-full bg-[#1B4332]/5 flex items-center justify-center mb-3">
                  <MessageSquare className="w-5 h-5 text-[#1B4332]/20" strokeWidth={1.5} />
                </div>
                <p className="text-[13px] text-[#1B4332]/50 font-medium mb-1">Start a conversation</p>
                <p className="text-[11px] text-[#1B4332]/30 max-w-xs">Ask about behavior, training, diet, or health concerns</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${idx}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full bg-[#1B4332]/10 flex items-center justify-center mr-2 mt-1 shrink-0">
                      <Sparkles className="w-3 h-3 text-[#1B4332]" strokeWidth={2} />
                    </div>
                  )}
                  <div className={`max-w-[75%] px-3.5 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-[#1B4332] text-white rounded-2xl rounded-br-sm'
                      : 'bg-[#F1F3F5] border border-[#E0E4E8] text-[#1B4332] rounded-2xl rounded-bl-sm'
                  }`}>
                    {msg.role === 'assistant' && (
                      <p className="text-[10px] font-bold text-[#1B4332]/40 uppercase tracking-wider mb-1">PurrCheck AI</p>
                    )}
                    <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/40' : 'text-[#1B4332]/25'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start" data-testid="loading-indicator">
                <div className="w-6 h-6 rounded-full bg-[#1B4332]/10 flex items-center justify-center mr-2 shrink-0">
                  <Sparkles className="w-3 h-3 text-[#1B4332]" strokeWidth={2} />
                </div>
                <div className="bg-[#F1F3F5] border border-[#E0E4E8] rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-[#1B4332]/30 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-[#1B4332]/30 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                    <div className="w-1.5 h-1.5 bg-[#1B4332]/30 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-4 py-3 border-t border-[#1B4332]/8 bg-white safe-bottom shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your cat's behavior..."
                className="flex-1 h-10 px-4 border border-[#1B4332]/10 rounded-full text-[13px] focus:border-[#1B4332]/30 focus:ring-2 focus:ring-[#1B4332]/5 transition-all outline-none bg-[#F8FAF9] placeholder:text-[#1B4332]/30"
                disabled={loading || !selectedCat}
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim() || !selectedCat}
                className="w-10 h-10 rounded-full bg-[#1B4332] hover:bg-[#2D6A4F] text-white flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <p className="text-[10px] text-[#1B4332]/25 mt-2 text-center tracking-wide">
              Educational guidance only — not a replacement for a veterinarian
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BehaviorChat;
